
import React, { useState, useEffect } from 'react';
import {
    Edit2,
    Trash2,
    Grid,
    CheckCircle,
    Search
} from 'react-feather';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { GiHotMeal } from "react-icons/gi";
import { BiCoffee, BiDrink } from "react-icons/bi";
import { MdOutlineIcecream } from "react-icons/md";
import './Categories.css';
import axios from 'axios';
import AddCategory from '../../../core/modals/category/addcategory';
import { base_api_url } from '../../../environment.jsx';
import SessionControl from '../../components/SessionControl/sessioncontrol';
import CheckRestauSelected from '../../components/SessionControl/checkrestauselected';
import { all_routes } from '../../../Router/all_routes';
import CatalogueNav from '../../components/CatalogueNav/CatalogueNav';


/**
 * Composant de gestion des catégories et sous-catégories.
 * Présente une interface claire pour l'organisation de la carte du restaurant.
 */


const Categories = () => {
    const MySwal = withReactContent(Swal);
    // Session and route control
    const route = all_routes;
    // UI and logic states
    const [termeRecherche, setTermeRecherche] = useState(''); // eslint-disable-line no-unused-vars

    const [categories, setCategories] = useState([]); // fetched categories
    const [loading, setLoading] = useState(false);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [reload, setReload] = useState(false);
    // Nouveaux états pour les types de catégories
    const [typeCategories, setTypeCategories] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [selectedType, setSelectedType] = useState(''); // eslint-disable-line no-unused-vars


    // --- Fonctions de chargement ---

    const fetchCategories = async () => {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("user"))?.access_token;
        const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;
        try {
            let url = `${base_api_url}api/categorie/${ideresto}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCategories(response.data.categorie || []);
            console.log("Catégories chargées :", response.data.categorie);
            localStorage.setItem("categorie", JSON.stringify(response.data.categorie));
        } catch (error) {
            setCategories([]);
            // Optionally handle error
            console.log(categories, loadingTypes) // eslint-disable-line no-unused-vars
        } finally {
            setLoading(false);
        }
    };

    const fetchTypeCategories = async () => {
        setLoadingTypes(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const response = await axios.get(`${base_api_url}api/typecategorie`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("Type categories loaded:", response.data);
            setTypeCategories(response.data || []);
        } catch (error) {
            console.error("Error fetching type categories:", error);
            // Optionnel : afficher une alerte utilisateur ici
        } finally {
            setLoadingTypes(false);
        }
    };

    // --- Hooks ---

    // Fetch categories and type categories from API
    useEffect(() => {
        fetchCategories();
    }, [reload]);

    useEffect(() => {
        fetchTypeCategories();
    }, []);


    // Regroupement dynamique des catégories par type de catégorie
    const groupCategories = () => {
        let filteredTypes = typeCategories;
        if (selectedType) {
            filteredTypes = typeCategories.filter(type => String(type.id) === String(selectedType));
        }
        return filteredTypes.map(type => ({
            id: type.id,
            label: type.nom,
            description: type.description,
            icone: getTypeIcon(type.nom),
            sousCategories: categories.filter(cat => cat.id_type === type.id)
        })).filter(group => group.sousCategories.length > 0);
    };

    // Fonction utilitaire pour choisir une icône selon le nom du type
    function getTypeIcon(typeNom) {
        const nom = (typeNom || '').toLowerCase();
        if (nom.includes('entrée')) return <BiCoffee size={16} />;
        if (nom.includes('plat')) return <GiHotMeal size={16} />;
        if (nom.includes('dessert')) return <MdOutlineIcecream size={16} />;
        if (nom.includes('boisson')) return <BiDrink size={16} />;
        return <Grid size={16} />;
    }

    // Filtrage par recherche
    const categoriesFiltrees = groupCategories().map(cat => ({
        ...cat,
        sousCategories: cat.sousCategories.filter(sc => {
            const nom = (sc.nom || '').toLowerCase();
            const term = (termeRecherche || '').toLowerCase();
            return nom.includes(term);
        })
    })).filter(cat => cat.sousCategories.length > 0);



    // Add/Edit modal logic
    const handleAddCategory = () => {
        setEditingCategory(null);
        setShowAddCategoryModal(true);
    };
    const handleEditCategory = (cat) => {
        setEditingCategory(cat);
        setShowEditCategoryModal(true);
        setShowAddCategoryModal(false);
    };
    const handleUpdateSuccess = () => {
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        setReload(r => !r);
    };

    const handleDeleteCategory = async (category) => {
        const result = await MySwal.fire({
            title: 'Êtes-vous sûr ?',
            text: `Voulez-vous vraiment supprimer la catégorie "${category.nom}" ? Cette action supprimera également les liens avec les articles associés !`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ff6a3d',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
            customClass: { popup: "pixel-toast-delete" }
        });

        if (result.isConfirmed) {
            setLoading(true);
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            try {
                await axios.put(`${base_api_url}api/categorie/${category.id_categorie || category.id}`, 
                    { status: 0 }, 
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                MySwal.fire({
                    icon: "success",
                    text: "Catégorie supprimée avec succès.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: { popup: "pixel-toast" },
                });

                setReload(r => !r);
            } catch (error) {
                console.error("Erreur lors de la suppression:", error);
                MySwal.fire({
                    icon: "error",
                    text: "Une erreur est survenue lors de la suppression.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    customClass: { popup: "pixel-toast" },
                });
            } finally {
                setLoading(false);
            }
        }
    };

    /* const handleToggleStatus = async (category) => {
        const token = JSON.parse(localStorage.getItem("user"))?.access_token;
        const currentStatus = category.statut || 'Visible';
        const newStatus = currentStatus === 'Visible' ? 'Masquée' : 'Visible';

        try {
            await axios.put(`${base_api_url}api/categorie/${category.id_categorie || category.id}`, 
                { ...category, statut: newStatus }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );

            MySwal.fire({
                icon: "success",
                text: `Catégorie ${newStatus === 'Visible' ? 'rendue visible' : 'masquée'} avec succès.`,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
                customClass: { popup: "pixel-toast" },
            });

            setReload(r => !r);
        } catch (error) {
            console.error("Erreur status:", error);
        }
    }; */

    const handleAddSuccess = () => {
        setShowAddCategoryModal(false);
        setEditingCategory(null);
        setReload(r => !r);
    };

    // UI rendering
    return (
        <div className="page-wrapper">
            {/* Session and restaurant selection control */}
            <SessionControl storageKey="user" propsup="email" redirectTo={route.signin} />
            <CheckRestauSelected storageKey="user" redirectTo={route.Restaurants} />
            <div className="categories-page">
                {/* NAVIGATION CATALOGUE */}
                <CatalogueNav
                    activeTabId="categories"
                    subtitle="Gestion des catégories."
                    addButtonText="Ajouter une sous-catégorie"
                    onAddClick={handleAddCategory}
                />


                {/* Barre de recherche */}
                <div style={{ marginBottom: 24, padding: "0 24px" }}>
                    <div style={{ position: 'relative', maxWidth: '320px' }}>
                        <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input
                            type="text"
                            placeholder="Rechercher une catégorie..."
                            value={termeRecherche}
                            onChange={e => setTermeRecherche(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 16px 12px 42px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#cbd5e1'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                </div>

                {/* CONTENU : Affichage des groupes de catégories */}
                <div className="categories-list">
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Chargement...</span>
                            </div>
                        </div>
                    ) : (
                        categoriesFiltrees.map((cat) => (
                            <div key={cat.id} className="category-group">
                                {/* En-tête du groupe (ex: Entrées) */}
                                <div className="group-header">
                                    <div className={`group-icon-wrapper icon-${cat.id}`} style={{ color: "#f96706", backgroundColor: "rgb(249 71 6 / 12%)" }}>
                                        {cat.icone}
                                    </div>
                                    <span className="group-title" style={{ textTransform: "capitalize" }}>{cat.label}</span>
                                    <span className="group-count">
                                        ({cat.sousCategories.length} {cat.sousCategories.length > 1 ? 'sous-catégories' : 'sous-catégorie'})
                                    </span>
                                </div>

                                {/* Liste des cartes de sous-catégories */}
                                {cat.sousCategories.map((sub) => (
                                    <div key={sub.id_categorie || sub.id} className="subcategory-card">
                                        <div className="sub-left">
                                            <span className="sub-name">{sub.nom}</span>
                                        </div>
                                        <div className="sub-right">
                                            <div className="tag-main-cat">{cat.label}</div>
                                            <div className="article-count">
                                                <Grid size={14} />
                                                <span>
                                                    {(sub.articles !== undefined ? sub.articles : (sub.nb_articles || 0))} {(sub.articles !== undefined ? sub.articles : (sub.nb_articles || 0)) > 1 ? 'articles' : 'article'}
                                                </span>
                                            </div>
                                            <div className="visible-badge">
                                                <CheckCircle size={12} />
                                                <span>{sub.statut || 'Visible'}</span>
                                            </div>

                                            {/* Actions directes */}
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <button
                                                    className="btn-actions"
                                                    title="Modifier"
                                                    onClick={() => handleEditCategory(sub)}
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                {/* <button
                                                    className="btn-actions"
                                                    title={sub.statut === 'Masquée' ? "Afficher" : "Masquer"}
                                                    onClick={() => handleToggleStatus(sub)}
                                                >
                                                    {sub.statut === 'Masquée' ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button> */}
                                                <button
                                                    className="btn-actions delete"
                                                    title="Supprimer"
                                                    style={{ color: '#ef4444' }}
                                                    onClick={() => handleDeleteCategory(sub)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Add Category Modal */}
            {showAddCategoryModal && (
                <AddCategory
                    show={showAddCategoryModal}
                    onClose={() => setShowAddCategoryModal(false)}
                    onFormSubmit={handleAddSuccess}
                    menuData={editingCategory}
                />
            )}
            {/* Edit Category Modal */}
            {showEditCategoryModal && editingCategory && (
                <AddCategory
                    show={showEditCategoryModal}
                    editMode={true}
                    CategorieData={editingCategory}
                    onFormSubmit={handleUpdateSuccess}
                    onClose={() => {
                        setShowEditCategoryModal(false);
                        setEditingCategory(null);
                    }}
                />
            )}
        </div>
    );
};

export default Categories;
